(() => {
  const key = 'bb:wishlist:v1';
  const tokenKey = 'bb:wishlist:customer-token:v1';
  const oauthKey = 'bb:wishlist:oauth:v1';
  const pendingChangeKey = 'bb:wishlist:pending-change:v1';
  const migrationPrefix = 'bb:wishlist:migrated:';
  const config = window.BBWishlistConfig || {};
  let discovery;
  let accountSnapshot;
  let synchronization;

  const normalizeID = (value) => {
    const id = String(value || '');
    if (!id) return '';
    return id.startsWith('gid://shopify/Product/') ? id : `gid://shopify/Product/${id}`;
  };

  const normalizeItem = (item) => ({
    ...item,
    id: normalizeID(item.id || item.productID),
    handle: String(item.handle || '')
  });

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value)
        ? value.map(normalizeItem).filter((item) => item.id && item.handle)
        : [];
    } catch {
      return [];
    }
  };

  const write = (items) => {
    const normalizedItems = items.map(normalizeItem).filter((item) => item.id && item.handle);

    try {
      localStorage.setItem(key, JSON.stringify(normalizedItems));
    } catch {
      return;
    }

    document.dispatchEvent(new CustomEvent('bb:wishlist-change', {
      detail: { items: normalizedItems, customerId: config.customerId || null }
    }));
  };

  const uniqueItems = (items) => {
    const unique = new Map();
    items.map(normalizeItem).forEach((item) => {
      if (item.id && item.handle && !unique.has(item.id)) unique.set(item.id, item);
    });
    return [...unique.values()];
  };

  const encodeBase64URL = (bytes) => {
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
  };

  const randomValue = () => encodeBase64URL(crypto.getRandomValues(new Uint8Array(32)));

  const codeChallenge = async (verifier) => {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return encodeBase64URL(new Uint8Array(digest));
  };

  const getDiscovery = async () => {
    if (discovery) return discovery;

    const [openidResponse, apiResponse] = await Promise.all([
      fetch('/.well-known/openid-configuration', { credentials: 'same-origin' }),
      fetch('/.well-known/customer-account-api', { credentials: 'same-origin' })
    ]);

    if (!openidResponse.ok || !apiResponse.ok) throw new Error('Shopify customer discovery failed.');

    discovery = {
      ...(await openidResponse.json()),
      ...(await apiResponse.json())
    };
    return discovery;
  };

  const readToken = () => {
    try {
      return JSON.parse(sessionStorage.getItem(tokenKey) || 'null');
    } catch {
      return null;
    }
  };

  const storeToken = (token) => {
    const previous = readToken();
    const stored = {
      ...previous,
      ...token,
      expires_at: Date.now() + (Number(token.expires_in || 300) * 1000)
    };
    sessionStorage.setItem(tokenKey, JSON.stringify(stored));
    return stored;
  };

  const exchangeToken = async (parameters) => {
    const endpoints = await getDiscovery();
    const response = await fetch(endpoints.token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: config.customerAccountClientId,
        ...parameters
      })
    });

    if (!response.ok) throw new Error('Shopify customer authentication failed.');
    return storeToken(await response.json());
  };

  const refreshAccessToken = async (token) => {
    if (!token?.refresh_token) return null;

    try {
      return await exchangeToken({
        grant_type: 'refresh_token',
        refresh_token: token.refresh_token
      });
    } catch {
      sessionStorage.removeItem(tokenKey);
      return null;
    }
  };

  const beginAuthorization = async () => {
    const endpoints = await getDiscovery();
    const verifier = randomValue();
    const state = randomValue();
    const nonce = randomValue();

    sessionStorage.setItem(oauthKey, JSON.stringify({
      verifier,
      state,
      returnUrl: window.location.href
    }));

    const authorizationURL = new URL(endpoints.authorization_endpoint);
    authorizationURL.search = new URLSearchParams({
      client_id: config.customerAccountClientId,
      scope: 'openid email customer-account-api:full',
      response_type: 'code',
      redirect_uri: config.customerAccountCallbackUri,
      state,
      nonce,
      code_challenge: await codeChallenge(verifier),
      code_challenge_method: 'S256'
    });

    window.location.assign(authorizationURL);
  };

  const completeAuthorization = async () => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const returnedState = url.searchParams.get('state');
    if (!code || !returnedState) return null;

    let attempt;
    try {
      attempt = JSON.parse(sessionStorage.getItem(oauthKey) || 'null');
    } catch {
      attempt = null;
    }

    if (!attempt || attempt.state !== returnedState) throw new Error('Shopify customer authentication state did not match.');

    const token = await exchangeToken({
      grant_type: 'authorization_code',
      code,
      redirect_uri: config.customerAccountCallbackUri,
      code_verifier: attempt.verifier
    });

    sessionStorage.removeItem(oauthKey);
    window.history.replaceState({}, '', attempt.returnUrl || config.customerAccountCallbackUri);
    return token;
  };

  const accessToken = async (interactive = false) => {
    const completed = await completeAuthorization();
    if (completed?.access_token) return completed.access_token;

    let token = readToken();
    if (token?.access_token && Number(token.expires_at || 0) > Date.now() + 30000) {
      return token.access_token;
    }

    token = await refreshAccessToken(token);
    if (token?.access_token) return token.access_token;

    if (interactive) await beginAuthorization();
    return null;
  };

  const customerRequest = async (query, variables = {}, interactive = false) => {
    const token = await accessToken(interactive);
    if (!token) return null;

    const endpoints = await getDiscovery();
    const response = await fetch(endpoints.graphql_api, {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query, variables })
    });

    if (response.status === 401) {
      sessionStorage.removeItem(tokenKey);
      if (interactive) await beginAuthorization();
      return null;
    }

    const result = await response.json();
    if (!response.ok || result.errors?.length) throw new Error('Shopify customer wishlist request failed.');
    return result.data;
  };

  const fetchAccountWishlist = async (interactive = false) => {
    const data = await customerRequest(`
      query CustomerWishlist {
        customer {
          id
          metafield(namespace: "custom", key: "wishlist") {
            value
            compareDigest
          }
        }
      }
    `, {}, interactive);

    if (!data) return null;

    let items = [];
    try {
      const decoded = JSON.parse(data.customer.metafield?.value || '[]');
      items = Array.isArray(decoded) ? decoded : [];
    } catch {
      items = [];
    }

    return {
      ownerID: data.customer.id,
      compareDigest: data.customer.metafield?.compareDigest || null,
      items: uniqueItems(items)
    };
  };

  const applyChange = (items, change) => {
    const updated = items.filter((candidate) => candidate.id !== change.item.id);
    if (change.isFavourite) updated.unshift(change.item);
    return uniqueItems(updated);
  };

  const saveAccountWishlist = async (snapshot, items, retry = true, change = null) => {
    const input = {
      namespace: 'custom',
      key: 'wishlist',
      ownerId: snapshot.ownerID,
      type: 'json',
      value: JSON.stringify(uniqueItems(items).map((item) => ({
        productID: item.id,
        handle: item.handle
      }))),
      compareDigest: snapshot.compareDigest
    };

    const data = await customerRequest(`
      mutation SetCustomerWishlist($metafields: [MetafieldsSetInput!]!) {
        metafieldsSet(metafields: $metafields) {
          metafields {
            value
            compareDigest
          }
          userErrors {
            field
            message
            code
          }
        }
      }
    `, { metafields: [input] });

    if (!data) return null;

    const error = data.metafieldsSet.userErrors?.[0];
    if (error?.code === 'STALE_OBJECT' && retry) {
      const latest = await fetchAccountWishlist();
      if (!latest) return null;
      const refreshedItems = change
        ? applyChange(latest.items, change)
        : uniqueItems([...latest.items, ...items]);
      return saveAccountWishlist(latest, refreshedItems, false, change);
    }
    if (error) throw new Error(error.message);

    return {
      ...snapshot,
      compareDigest: data.metafieldsSet.metafields?.[0]?.compareDigest || null,
      items: uniqueItems(items)
    };
  };

  const hydrateItem = async (item, localItems) => {
    const local = localItems.find((candidate) => candidate.id === item.id);
    if (local?.title && local?.image) return local;

    try {
      const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`);
      if (!response.ok) return item;
      const product = await response.json();
      return {
        ...item,
        title: product.title,
        url: product.url || `/products/${item.handle}`,
        image: product.featured_image || '',
        price: new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP'
        }).format(Number(product.price || 0) / 100)
      };
    } catch {
      return item;
    }
  };

  const hydrateItems = async (items) => {
    const localItems = read();
    return Promise.all(items.map((item) => hydrateItem(item, localItems)));
  };

  const synchronizeFromAccount = async (interactive = false) => {
    if (!config.customerAccountClientId) return;
    if (synchronization) return synchronization;

    synchronization = (async () => {
      let snapshot = await fetchAccountWishlist(interactive);
      if (!snapshot) return;

      let pendingChange;
      try {
        pendingChange = JSON.parse(sessionStorage.getItem(pendingChangeKey) || 'null');
      } catch {
        pendingChange = null;
      }

      if (pendingChange?.item) {
        const change = {
          item: normalizeItem(pendingChange.item),
          isFavourite: Boolean(pendingChange.isFavourite)
        };
        const changedItems = applyChange(snapshot.items, change);
        snapshot = await saveAccountWishlist(snapshot, changedItems, true, change);
        sessionStorage.removeItem(pendingChangeKey);
      }

      const migrationKey = `${migrationPrefix}${snapshot.ownerID}`;
      const hasMigrated = localStorage.getItem(migrationKey) === 'true';
      let accountItems = snapshot.items;

      if (!hasMigrated) {
        accountItems = uniqueItems([...snapshot.items, ...read()]);
        accountSnapshot = await saveAccountWishlist(snapshot, accountItems);
        localStorage.setItem(migrationKey, 'true');
      } else {
        accountSnapshot = snapshot;
      }

      write(await hydrateItems(accountItems));
      updateButtons();
    })().finally(() => {
      synchronization = null;
    });

    return synchronization;
  };

  const pushChange = async (item, isFavourite) => {
    if (!config.customerAccountClientId) return;

    const change = { item, isFavourite };
    sessionStorage.setItem(pendingChangeKey, JSON.stringify(change));
    const snapshot = await fetchAccountWishlist(true);
    if (!snapshot) return;

    const items = applyChange(snapshot.items, change);
    accountSnapshot = await saveAccountWishlist(snapshot, items, true, change);
    sessionStorage.removeItem(pendingChangeKey);
  };

  const updateButtons = () => {
    const items = read();

    document.querySelectorAll('[data-bb-wishlist-count]').forEach((count) => {
      count.textContent = items.length > 99 ? '99+' : String(items.length);
      count.hidden = items.length === 0;
    });

    document.querySelectorAll('[data-bb-wishlist-button]').forEach((button) => {
      const productID = normalizeID(button.dataset.productId);
      const active = items.some((item) => item.id === productID);
      const label = button.querySelector('[data-bb-wishlist-label]');
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? window.BBWishlistConfig.removeLabel : window.BBWishlistConfig.addLabel);

      if (label) {
        label.textContent = active ? window.BBWishlistConfig.removeShort : window.BBWishlistConfig.addShort;
      }
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-bb-wishlist-button]');
    if (!button) return;

    const items = read();
    const item = normalizeItem({
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice
    });
    const existingIndex = items.findIndex((candidate) => candidate.id === item.id);
    let isFavourite;

    if (existingIndex >= 0) {
      items.splice(existingIndex, 1);
      isFavourite = false;
    } else {
      items.unshift(item);
      isFavourite = true;
    }

    write(items);
    updateButtons();
    pushChange(item, isFavourite).catch(() => {});
  });

  document.addEventListener('shopify:section:load', updateButtons);
  document.addEventListener('DOMContentLoaded', () => {
    write(read());
    updateButtons();

    const hasToken = Boolean(readToken()?.access_token);
    const isWishlistPage = window.location.pathname === new URL(config.customerAccountCallbackUri || window.location.href).pathname;
    const isOAuthCallback = new URL(window.location.href).searchParams.has('code');

    if (hasToken || isWishlistPage || isOAuthCallback) {
      synchronizeFromAccount(isWishlistPage || isOAuthCallback).catch(() => {});
    }
  });

  window.BBWishlist = {
    read,
    write,
    refresh: updateButtons,
    synchronize: () => synchronizeFromAccount(true)
  };
})();
